/**
 * Tabela de registros-fonte das telas de drill-down (Task 10) · redesign R6.
 *
 * É deliberadamente "burra": recebe linhas já selecionadas pelo seletor do KPI
 * e as exibe. Cada linha carrega o id do registro (`data-record-id`) para que
 * o aceite de "mesmos registros" seja verificável no DOM/markup, não só no
 * cálculo.
 *
 * Redesign R6 (apresentação apenas): visual de tabela densa — `<caption>`
 * (scope/role preservados), cabeçalho com destaque, zebra sutil nas linhas,
 * contêiner com rolagem horizontal em telas estreitas (a tabela nunca estoura o
 * layout em 360). TODOS os hooks semânticos (`caption`, `<th scope>`,
 * `data-record-id`, `data-selected`/`aria-current`) são PRESERVADOS — só
 * entram classes utilitárias Tailwind. Nada de cor como único indicador: a
 * linha selecionada combina `aria-current` + estilo.
 */
import { createElement as h, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { rowStaggerDelayMs } from "./motion.js";

export interface RecordTableColumn<TRow> {
  key: string;
  label: string;
  /** Conteúdo da célula; pode devolver texto ou um link para o registro. */
  render: (row: TRow) => ReactNode;
  /** Alinhamento numérico à direita (apresentação; default esquerda). */
  align?: "left" | "right";
  /** Texto longo (JSON, descrição): quebra linha numa coluna de largura mínima em vez de esticar a tabela. */
  wrap?: boolean;
}

export interface RecordTableProps<TRow> {
  caption: string;
  columns: RecordTableColumn<TRow>[];
  rows: TRow[];
  idOf: (row: TRow) => string;
  /** Mensagem honesta quando não há registro (nunca inventar linha). */
  emptyMessage: string;
  /**
   * Registro que o drill-down pediu (Task 13): a linha correspondente é
   * marcada (`data-selected`/`aria-current`) para o destino mostrar O MESMO
   * registro de onde o operador clicou, e não apenas o recorte dele.
   *
   * Marcar em vez de filtrar é deliberado: o registro pedido aparece no seu
   * contexto (as outras propostas/negociações do cliente continuam visíveis),
   * que é o que a tela de destino existe para mostrar.
   */
  selectedId?: string | null;
  /** Oculta a `<caption>` visualmente (continua no DOM para leitor de tela). */
  hideCaption?: boolean;
  /**
   * Entrada escalonada das linhas (Fase 6, Plano de Ataque R8) — opt-in, por
   * tela: default `false` para o rollout mecânico das fases 7/8/10 ligar
   * tela a tela, sem mudar o markup de todo consumidor de `RecordTable` de
   * uma vez. Puramente de apresentação: a ordem/conteúdo das linhas não muda.
   */
  animateRows?: boolean;
  /** Sem moldura própria (borda/sombra/legenda visível): a tabela vive DENTRO de um cartão, sem caixa dentro de caixa. */
  bare?: boolean;
}

// Redesign admin: célula curta não quebra (selos, SKU, datas); coluna `wrap` quebra com teto de largura.
const CELL = "whitespace-nowrap px-4 py-3 text-sm text-body align-middle";
const CELL_WRAP = "min-w-[14rem] max-w-[24rem] whitespace-normal break-words px-4 py-3 text-sm text-body align-middle";
const CELL_RIGHT = `${CELL} text-right tabular-nums`;

export function RecordTable<TRow>({
  caption,
  columns,
  rows,
  idOf,
  emptyMessage,
  selectedId = null,
  hideCaption = false,
  animateRows = false,
  bare = false
}: RecordTableProps<TRow>): ReactElement {
  if (!rows.length) {
    return h(
      "p",
      {
        className: "record-table__empty rounded-panel border border-border bg-surface px-4 py-6 text-sm text-body",
        role: "status"
      },
      emptyMessage
    );
  }
  return h(
    // Contêiner com rolagem horizontal: em 360 a tabela densa rola em vez de
    // estourar o layout (régua: 1 col/empilhado sem overflow na página).
    // `tabIndex=0` + `role="region"` + nome acessível (a caption) tornam a área
    // rolável OPERÁVEL POR TECLADO (axe scrollable-region-focusable / WCAG 2.1.1).
    "div",
    {
      // `overflow-y-hidden`: com só `overflow-x-auto` o CSS transforma o eixo Y em `auto` e a
      // animação de entrada das linhas (translateY) criava uma barra vertical dentro da tabela.
      className:
        "record-table-wrap overflow-x-auto overflow-y-hidden focus-visible:outline-none " +
        (bare ? "" : "rounded-panel border border-border bg-surface shadow-s1"),
      tabIndex: 0,
      role: "region",
      "aria-label": caption
    },
    h(
      "table",
      { className: "record-table w-full border-collapse text-left" },
      h(
        "caption",
        {
          className: hideCaption || bare
            ? "sr-only"
            : "px-4 py-3 text-left text-base font-extrabold text-ink"
        },
        caption
      ),
      h(
        "thead",
        null,
        h(
          "tr",
          { className: "border-b border-border-strong bg-surface-soft" },
          columns.map((column) =>
            h(
              "th",
              {
                key: column.key,
                scope: "col",
                className:
                  "whitespace-nowrap px-4 py-3 text-xs font-bold uppercase tracking-wide text-muted " +
                  (column.align === "right" ? "text-right" : "text-left")
              },
              column.label
            )
          )
        )
      ),
      h(
        "tbody",
        null,
        rows.map((row, index) => {
          const id = idOf(row);
          const selected = selectedId !== null && id === selectedId;
          return h(
            "tr",
            {
              key: id,
              // `data-record-id` + `data-selected`/`aria-current` ficam ADJACENTES
              // (sem className entre eles): o aceite de drill-down verifica
              // `data-record-id="..." data-selected="true"` no markup servido.
              "data-record-id": id,
              ...(selected ? { "data-selected": "true", "aria-current": "true" as const } : {}),
              className:
                "border-b border-border bg-surface last:border-b-0 " +
                "transition-colors duration-fast ease-s2 hover:bg-surface-soft " +
                (selected ? "bg-brand-wash ring-1 ring-inset ring-brand" : "") +
                (animateRows ? " s2-row-in" : ""),
              // Entrada escalonada (Fase 6) — opt-in via `animateRows`. Mesma
              // linha, mesmo conteúdo; só o atraso de entrada é novo.
              ...(animateRows ? { style: { animationDelay: `${rowStaggerDelayMs(index)}ms` } as CSSProperties } : {})
            },
            columns.map((column) =>
              h("td", { key: column.key, className: column.wrap ? CELL_WRAP : column.align === "right" ? CELL_RIGHT : CELL }, column.render(row))
            )
          );
        })
      )
    )
  );
}
